import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { SidePanelService } from '@lx/quote-tool/lite/shared/services';
import { SidePanelComponent } from '../../layout';
import { CurrencyListComponent } from '../currency-list/currency-list.component';

/**
 * Currency selector component - presentational component for selecting currencies
 * @description Pure presentational component that receives data via inputs and emits events via outputs
 */
@Component({
  standalone: true,
  selector: 'lx-currency-selector',
  templateUrl: './currency-selector.component.html',
  styleUrls: ['./currency-selector.component.scss'],
  imports: [CommonModule, SidePanelComponent, CurrencyListComponent],
})
export class CurrencySelectorComponent {
  /**
   * The currently selected currency code
   */
  @Input() currency: string | undefined;

  /**
   * Whether the component is in single currency mode (no selection available)
   */
  @Input() singleCurrencyMode: boolean | undefined;

  /**
   * List of available currencies to choose from
   */
  @Input() availableCurrencies: string[] = [];

  /**
   * Emits when a new currency is selected by the user
   */
  @Output() currencySelected = new EventEmitter<string>();

  /**
   * Emits when the currency selection dialog visibility changes
   */
  @Output() dialogVisibilityChange = new EventEmitter<boolean>();

  protected dialogVisible = false;

  private readonly sidePanelService = inject(SidePanelService);

  /**
   * Opens the currency selection dialog
   * @protected
   */
  protected openCurrencyDialog(): void {
    this.changeDialogVisibility(true);
  }

  /**
   * Handles currency selection and notifies parent component
   * @param currency - The selected currency code
   * @protected
   */
  protected selectCurrency(currency: string): void {
    this.sidePanelService.hidePanel(() => {
      this.currencySelected.emit(currency);
      this.changeDialogVisibility(false);
    });
  }

  /**
   * Hides the currency selection dialog
   * @protected
   */
  protected hideDialog(): void {
    this.sidePanelService.hidePanel(() => this.changeDialogVisibility(false));
  }

  /**
   * Changes dialog visibility state and emits event
   * @param dialogVisible - Whether the dialog should be visible
   * @private
   */
  private changeDialogVisibility(dialogVisible: boolean): void {
    this.dialogVisible = dialogVisible;
    this.dialogVisibilityChange.emit(dialogVisible);
  }
}
